import { styled } from "@mui/system";
import { colors, tokens } from "../../../utils";
import { mq } from "../../../utils/breakpoints";

// ISP-9999 (Gordon 2026-08-19, screenshots): Gallery's header rendered visibly
// TALLER than Discover's. MEASURED live on web-stg @1440x900 (both routes,
// same shared GalleryHeader component — there is no second header component):
// Gallery = 90px (its declared `height:10vh` of a 900px viewport, un-squeezed).
// Discover = 79.14px -- NOT a different declared height at all: Discover's own
// page wrapper (pages/ExploreV2/index.jsx) bounds its content to a strict
// `height:100vh; overflow:hidden` flex COLUMN so its virtualized feed can own
// the only scrollbar. MainContainer, as a plain flex item with no flex-shrink
// override, was getting proportionally SQUEEZED by that column's flexbox math
// (10vh's natural 90px didn't fit alongside the toolbar+feed within 100vh) --
// an accidental side effect of one page's layout, not an intended header size.
// Gordon liked the tighter, squeezed-Discover look and wants it EVERYWHERE,
// deterministically, so it can never again depend on which page happens to
// squeeze it. Fix: a single fixed-px HEADER_HEIGHT (80px -- the app's own
// spacing scale, spacing.lg*5 / spacing.sm*10, src/theme/tokens.js; 0.86px
// off Discover's accidental 79.14px, an imperceptible visual match) replaces
// the viewport-relative 10vh, and `flexShrink:0`/`flexGrow:0`/`minHeight`
// pin it immune to ANY ancestor flex layout on ANY route -- the one thing
// the old 10vh value never had, which is exactly why Gallery and Discover
// could silently drift apart in the first place. GalleryHeader is already the
// ONE shared header every authenticated page renders (see the G3/G4 note
// above), so this single change converges every page's header to the same
// height by construction, not by editing each page.
//
// header-tighter-messenger-parity-0916 (Gordon, 2026-09-16, Facebook/Messenger
// screenshots side by side: "look how much tighter Facebook's headers are than
// ours - reduce the height/margins of the header to match theirs"). MEASURED
// live on web-dev (main.43b822a7.js, 1440 + 390, headless): bar 80px, nav
// icon boxes 40px with a 16px gap, bell IconButton 50px (42px on phone),
// glyphs 26px (Share 22px). Messenger's top bar is 56px with 40px circular
// buttons, 24px glyphs and 8px gaps. Every value below is now that geometry,
// and each is ONE exported token so page slots/offsets derive from it (e.g.
// Gallery's header slot, the legacy NotificationDropDown's top) instead of a
// second hardcoded copy that would make content jump when this changes.
export const HEADER_HEIGHT = 56;
// The square hit box of every header icon button (nav icons + bell).
export const HEADER_ICON_BUTTON_SIZE = 40;
// Visible gap between two adjacent header icon buttons (half on each side).
export const HEADER_ICON_GAP_PX = 8;

export const MainContainer = styled("div")({
  height: `${HEADER_HEIGHT}px`,
  minHeight: `${HEADER_HEIGHT}px`,
  flexShrink: 0,
  flexGrow: 0,
  boxSizing: "border-box",
  backgroundColor: colors.background,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "0 4px 7px #00000014",
});

// G3 (coverage doc): GalleryHeader is the ACTUAL persistent app header on
// every authenticated route it's mounted on (Wrapper renders GalleryHeader,
// not Navbar — Navbar is a separate, narrowly-used component; a first-pass
// fix landed on Navbar by mistake and an independent qa-validator critic
// caught it 2026-08-06, see gauntlet-a11y3-critic-verdict.md). Wraps the
// header's real content in a `<nav>` landmark. `display: contents` removes
// the element's own box from layout so it does NOT become a 3rd flex item
// against MainContainer's `display:flex; justify-content:space-between`
// (which expects exactly 2 items, the left/right FlexContainer blocks) —
// same technique already proven safe in this exact file for SecondaryActions
// (line ~126, ISP-4018 phone-header-fold) and OverflowOnly.
export const NavRegion = styled("nav")({
  display: "contents",
});

// The gray shadow-highlight shown on hover. ISP-9999 reuses this SAME token as
// the current-section "selected" background, so active and hover are visually
// identical by construction (no new color, and they coexist harmlessly).
export const NAV_ICON_HIGHLIGHT_BG = "#dee2e66e";

// kelly-0915-header-icons-same-size: the ONE glyph-size token for every bare
// line-art icon in the header's primary nav cluster (Home/Discover/Capture/
// Chat/Notifications/overflow-kebab). MEASURED live on web-dev
// (main.6f22a36b.js, 2026-09-15): five of these six sites already hardcoded
// the literal `26` independently (HomeNavEntry, CaptureNavEntry, ChatNavEntry,
// ExploreNavEntry, HeaderOverflowMenu's kebab) with no shared source, and the
// sixth (NotificationCenter's bell) set no size at all, silently inheriting
// MUI's 24px default -- one visibly smaller icon in the row with nothing
// pinning it to match its neighbours. 26 is the value every other site already
// intended; this just gives that intent one place to live instead of five.
// header-tighter-messenger-parity-0916: 26 -> 24 (Messenger glyph size), which
// also fits the 40px button's 8px padding exactly (40 - 2*8 = 24).
export const HEADER_ICON_SIZE = 24;
// Padding that centres a HEADER_ICON_SIZE glyph inside a HEADER_ICON_BUTTON_SIZE box.
export const HEADER_ICON_BUTTON_PADDING_PX =
  (HEADER_ICON_BUTTON_SIZE - HEADER_ICON_SIZE) / 2;

export const IconWrapper = styled("div", {
  // `isActive` is a styling-only flag (current-section highlight); keep it off
  // the DOM node so React doesn't warn about an unknown attribute. The public
  // `aria-current="page"` marker (set by each nav entry) is what reaches the DOM.
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ isActive }) => ({
  height: `${HEADER_ICON_BUTTON_SIZE}px`,
  width: `${HEADER_ICON_BUTTON_SIZE}px`,
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  borderRadius: "4px",
  padding: `${HEADER_ICON_BUTTON_PADDING_PX}px`,
  // ISP-9999 (Gordon 2026-08-19, screenshot): the header nav cluster had too
  // much horizontal air between icons. Was "2px 10px" (20px gap between
  // adjacent icons); tightened to the app's own 4-based spacing scale
  // (spacing.sm = 8px, src/theme/tokens.js) -> 16px gap. Still >=8px, clearing
  // the adjacent-touch-target floor asserted below.
  // header-tighter-messenger-parity-0916: 16px -> 8px gap (Messenger parity),
  // no vertical margin (the 56px bar centres the 40px box on its own).
  margin: `0px ${HEADER_ICON_GAP_PX / 2}px`,
  transition: "box-shadow 0.2s ease",

  // kelly-0915-header-icons-same-size: this box is `display:flex` with an
  // 8px padding on every side, leaving a 24x24 content box for a 26x26 icon.
  // A bare <svg>/<img> child has the browser default `flex-shrink:1`, so the
  // flex row auto-shrank its WIDTH (the main axis) to fit that 24px box while
  // its height (the cross axis, never auto-shrunk) stayed at 26 -- a squished
  // 24x26 icon instead of a 26x26 square. MUI's <SvgIcon> escaped this (it
  // sets its own flex-shrink:0), which is why the Discover icon measured a
  // true 26x26 live while Home/Capture/Chat measured 24x26 on the exact same
  // requested size. `flex-shrink:0` on every icon child fixes the distortion
  // for good, for any icon dropped into this wrapper, not just today's three.
  "& > svg, & > img": {
    flexShrink: 0,
  },

  "&:active": {
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
  },

  "&:hover": {
    backgroundColor: NAV_ICON_HIGHLIGHT_BG,
  },

  // ISP-9999 (Gordon 2026-08-12): the icon for the section you're currently in
  // persists that same hover highlight so it reads as SELECTED. Hover still
  // works (identical value), so active + hover coexist.
  ...(isActive ? { backgroundColor: NAV_ICON_HIGHLIGHT_BG } : null),
}));

export const Icon = styled("img")({
  width: "100%",
  height: "100%",
});

// header-tighter-messenger-parity-0916: the ONE unread-count badge look for the
// header (Messages ChatBadge + the Notifications bell's MUI Badge). Messenger
// geometry — an 18px circle, 11px bold white digits, ringed in the bar colour
// so it reads cleanly where it overlaps the glyph's top-right — in OUR purple.
// MEASURED before this change the bell used MUI's stock `color="error"` badge
// (red, 20px tall, 12px/500 digits), so the two header badges did not match.
export const HEADER_BADGE_SIZE_PX = 18;
export const HEADER_BADGE_FONT_PX = 11;
export const HEADER_BADGE_STYLE = {
  minWidth: HEADER_BADGE_SIZE_PX,
  height: HEADER_BADGE_SIZE_PX,
  padding: "0 5px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  // a11y: white 11px digits on PrimaryPurple500 (#9B5AF8) measured 4.03:1 on
  // web-stg — below the WCAG AA 4.5:1 floor for normal-size text. Use the
  // on-fill brand token (#8F38EF, 5.28:1), the same one the inbox unread badge
  // already uses, so the header badges match and both clear AA.
  backgroundColor: tokens.brand.primaryActive,
  color: colors.white,
  fontFamily: '"DM Sans", sans-serif',
  fontSize: HEADER_BADGE_FONT_PX,
  fontWeight: 700,
  lineHeight: 1,
  // A px string, not a number: in MUI `sx` a bare number is a theme.shape
  // multiplier (9 -> 36px), which would turn the circle into a blob.
  borderRadius: `${HEADER_BADGE_SIZE_PX / 2}px`,
  boxShadow: `0 0 0 2px ${colors.background}`,
};

// GR-2: Facebook-style unread badge for the header chat-indicator (ChatNavEntry).
// Overlaid on the icon's top-right. No motion → nothing to reduce. Look comes
// from HEADER_BADGE_STYLE (which carries the AA-safe tokens.brand.primaryActive fill).
export const ChatBadge = styled("span")({
  position: "absolute",
  top: 2,
  right: 2,
  ...HEADER_BADGE_STYLE,
  pointerEvents: "none",
});

// The same badge look applied to MUI's <Badge> (the Notifications bell), passed
// straight to its badge slot so it lands in the badge's OWN class after MUI's
// base badge rules (a parent `& .MuiBadge-badge` selector would race MUI's
// stock 20px/12px rules on stylesheet order).
export const headerMuiBadgeSlotProps = {
  badge: { sx: HEADER_BADGE_STYLE },
};

// ISP-9999 / QA-149 #2 (Kelly/Gordon, 2026-08-20): "search bar window right
// edge aligns with the business box's side edge." MEASURED live (mocked
// local render, 1280/1440/1920px viewports): the SearchBar's Container
// started 72.8-79.2px from the header's left edge (this margin + the logo's
// own footprint below), the 1vw component drifting with viewport width — so
// the earlier QA-149 fix (SearchBar's max-width matching BUSINESS_BOX_WIDTH_PX)
// could never land the two right edges on the same pixel. Fixed-px values
// here make that left inset a single deterministic number that
// SearchBar/styles.js can subtract out; see HEADER_SEARCH_BAR_LEFT_INSET_PX.
export const FLEX_CONTAINER_MARGIN_PX = 20;
// QA-17 header/profile logo size mismatch: the header logo rendered at 40px
// while the Profile dropdown avatar (ProfileMenu's WheelComponent, 46px +
// UserContainer's 5px bottom padding / 10px right margin = a ~56x51 visual
// footprint) reads noticeably bigger, so the two brand marks in the same
// header row don't match. A first pass bumped this to 52 to visually
// approximate that ~56x51 outer footprint — CLOSE but not equal.
//
// header-logo-size-match-profile-dropdown RE-FIX (Gordon, 2026-08-26): PR
// #4613 changed the comparable quantity to each control's own inner "mark"
// (avatar clamped to 46px, EXCLUDING UserContainer's paddingBottom/marginRight
// as "incidental spacing") and shrank the logo to 46px to match it. That was
// wrong — Gordon's ask is a match of the two CONTROLS as actually rendered,
// and a control's own padding/margin is part of what a user sees and measures.
// MEASURED live on the deployed bundle (e0d4a5c2) after #4613: logo 46x46 vs
// avatar-wrapper (UserContainer, i.e. the 46px avatar + its own
// marginRight:10 (ProfileMenu/index.jsx) + the container's own
// paddingBottom:5 (ProfileMenu/styles.js)) = 56x51 — delta 10x5, WORSE than
// the pre-#4613 52x52 approximation's delta of 4x1. 56 and 51 differ by 5px,
// so no single square value can land both within 1px of their targets —
// two explicit dimensions are needed, reusing the avatar-wrapper's own
// numbers (46+10=56, 46+5=51) so the two controls render pixel-equal.
// (HEADER_SEARCH_BAR_LEFT_INSET_PX below derives from the width, so the
// search bar's alignment stays correct automatically.)
export const LOGO_WIDTH_PX = 56;
export const LOGO_HEIGHT_PX = 51;
// spacing.md (theme/tokens.js) — replaces the old "1vw" gap, which was the
// source of the viewport-dependent drift above.
export const LOGO_MARGIN_RIGHT_PX = 12;
export const HEADER_SEARCH_BAR_LEFT_INSET_PX =
  FLEX_CONTAINER_MARGIN_PX + LOGO_WIDTH_PX + LOGO_MARGIN_RIGHT_PX;

// M390-01 (mobile-390 sweep vY, MEASURED on main.708ccddf.js): at 360px the
// header still overflowed (documentElement.scrollWidth 386 vs clientWidth 360,
// ~26px) with the avatar/.profile-menu clipped past the right edge — 390/430
// measured CLEAN. The ISP-4018 phone fold (OverflowOnly/SecondaryActions above)
// already removed the big secondary-action cluster below 600px; what's left at
// 360 is just this fixed FLEX_CONTAINER_MARGIN_PX (20px, tuned for >=390) eating
// into a viewport with ~30px less room to give. Tightening the margin ONLY below
// this cut (390/430 stay byte-identical — this query never matches there) frees
// up to 4 * (FLEX_CONTAINER_MARGIN_PX - NARROW_PHONE_MARGIN_PX) = 32px, enough to
// close the measured 26px gap without touching the already-clean 390/430 layout.
export const NARROW_PHONE_MAX_PX = 375;
export const NARROW_PHONE_MARGIN_PX = 12;

// mobile390-header-profilemenu-overflow (2026-08-27, MEASURED live on
// main.708ccddf.js): the phone fold above closed the OLD 226px overflow at
// 390/430, but two persistent controls added AFTER that fold — the
// NotificationCenter bell (flag-gated ON) and, on Discover only, the
// icon-only CreateWheelNavEntry — were never part of that fold's width
// budget. Their extra footprint reopened a SMALLER overflow at the full
// phone band itself (390 measured scrollWidth 413-421 vs clientWidth 390,
// .profile-menu right edge 411-420): /discover, /notifications, /settings,
// /analytics, /referrals. NARROW_PHONE_MARGIN_PX above only fires <=375, so
// it never reached this band. Applying the SAME reduction across the whole
// phone band (<600, via `mq.phone`) claws back 8px/side here; combined with
// OverflowOnly's own phone-band trim below and NotificationCenter's
// phone-sized bell, that's enough to close the measured 21-31px gap without
// touching the already-locked <=375 contract (still emits its own, separate
// `max-width: 375px` rule — see GalleryHeader.narrowPhoneMargin.test.jsx).
//
// header-390-overflow-reopened (Gordon, 2026-08-27, MEASURED live on
// main.5780ca85.js, authed, 390x844, AFTER #4689's tabletP fold): /discover
// and /gallery/21 still scrollWidth ~432 vs clientWidth 390 (~42px), with
// .profile-menu's rect at left=376/right=432 — same defect reopens at
// clipped-ancestor routes too (/capture right=409, no doc scroll there).
// Root cause: BOTH the left instance (logo + SearchBar) and this right
// instance (NotificationCenter bell / CreateWheelNavEntry / the ⋮ trigger /
// the avatar) are the SAME styled component, so `minWidth: 0` above — added
// so the left instance's SearchBar can shrink below its own content size —
// also applies to the right instance. That lets the BROWSER shrink the
// right instance's own box narrower than its (non-text, non-shrinking) icon
// children's combined min-content width once MainContainer runs out of
// room; the children don't shrink with it (fixed-size icon boxes, no
// min-width:0 of their own), so they spill out of the narrowed box instead
// — exactly the measured overflow, landing on whichever control is last
// (.profile-menu). Passing `noShrink` on the right instance restores this
// container's natural default `min-width: auto` (i.e. its own min-content
// size, the sum of its fixed-size children) and pins `flex-shrink: 0`, so
// MainContainer can never squeeze it below what its content needs — the
// left instance (still `minWidth: 0`, still the one component with a truly
// shrinkable child in SearchBar's own `min-width:0` tabletP rule) absorbs
// 100% of the squeeze instead, exactly as it already does today whenever
// there isn't enough combined room.
export const FlexContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "noShrink",
})(({ noShrink }) => ({
  display: "flex",
  justifyContent: "space-evenly",
  alignItems: "center",
  margin: `0px ${FLEX_CONTAINER_MARGIN_PX}px`,
  // Lets a shrinking child (e.g. SearchBar's width:100%) actually shrink below
  // its own content size instead of forcing the row wider than the viewport.
  // The right-hand icon/avatar cluster opts OUT via `noShrink` — see the
  // header-390-overflow-reopened note above.
  minWidth: noShrink ? "auto" : 0,
  ...(noShrink ? { flexShrink: 0 } : null),
  [mq.phone]: {
    margin: `0px ${NARROW_PHONE_MARGIN_PX}px`,
  },
  [`@media (max-width: ${NARROW_PHONE_MAX_PX}px)`]: {
    margin: `0px ${NARROW_PHONE_MARGIN_PX}px`,
  },
}));

export const LogoContainer = styled("div")({
  marginRight: `${LOGO_MARGIN_RIGHT_PX}px`,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  borderRadius: "4px",
  // The logo is a keyboard-operable "home" control (role="button"): give it a
  // visible focus ring so keyboard users can see it (WCAG 2.4.7). :focus-visible
  // only, so a mouse click never paints the ring.
  "&:focus": { outline: "none" },
  "&:focus-visible": {
    outline: `2px solid ${colors.PrimaryPurple500}`,
    outlineOffset: "2px",
  },
});

// gordon-0918-header-logo-blurry-upper-left-staging (MEASURED on web-dev):
// iyugaBetaLogo.svg's own viewBox is 47x45 (aspect ~1.044), but the logo
// renders into a 56x51 box (aspect ~1.098) to match the avatar control's
// footprint above. A plain <img> defaults to object-fit:fill, so the browser
// ANISOTROPICALLY STRETCHED the asset (vector BETA-ribbon paths included) to
// the mismatched box, softening edges — not a source-resolution shortfall
// (the embedded raster is 2043x1508, already confirmed oversampled for this
// box). object-fit:contain scales uniformly instead.
export const LogoImg = styled("img")({
  objectFit: "contain",
});

export const AddWheelButton = styled("div")({
  cursor: "pointer",
});

export const CenterContainer = styled("div")({
  paddingRight: "10vw",
});

export const Beta = styled("img")({
  width: 70,
  height: 32,
  marginRight: 10,
  cursor: "pointer",
});

// ── Phone/tablet-portrait header fold (ISP-4018 · responsive system §2 "App
// shell & navigation per breakpoint"). MEASURED defect this exists to kill: at
// 390px the header's right-hand action cluster needs 444px on its own, so the
// whole bar measures 602px against a 376px client width — 226px of horizontal
// page overflow on /settings, /analytics and /referrals, and (where an
// ancestor clips instead of scrolling) the avatar/ProfileMenu pushed
// off-screen at x=502 on /capture and /notifications, i.e. unreachable.
//
// gallery-header-768-tablet-overflow (Gordon, 2026-08-27, MEASURED live on
// main.ff3172c5.js, /gallery/21 @768x…, authed): the avatar's right edge sat
// at x=783 — 15px past the 768px viewport (29px past the header's own 754px
// box) — while 1440 measured clean (right=1397, -43px margin). Root cause:
// this fold previously only fired below `mq.phone` (<600px). 768 falls in the
// tabletP band (600-899, `mq.tabletP`) where NONE of the right-cluster's
// fixed-width icons (IconWrapper 40px + margin, the 46px avatar, the Invite
// button) shrink or wrap, so the same unbounded-cluster defect that hit 390
// reopens one breakpoint tier up. `mq.tabletP` (<=899px) is a strict superset
// of `mq.phone` (<=599px), so widening the cut to it keeps every already-fixed
// phone width (360/375/390/430) byte-identical and additionally folds the
// cluster across the whole 600-899 tabletP band, closing 768 without touching
// tabletL (900-1199) or desktop (>=1200, still 2 items and clean at 1440).
//
// The responsive system's phone row specifies a slim header of "logo + bell +
// avatar"; the tabletL row specifies that "≥3 secondary header actions collapse
// into one ⋮ menu". This wrapper implements exactly that at the tabletP cut: the
// secondary actions (Feedback · Capture · Messages · Following · Invite) leave
// the bar and reappear as labeled rows in HeaderOverflowMenu. Nothing is
// removed — every action stays reachable, and now with a text label.
//
// `display: contents` above the cut is deliberate: the wrapper generates NO box,
// so at tablet-landscape/desktop the children remain direct flex items of
// FlexContainer and the rendered geometry is identical to before this change.
export const SecondaryActions = styled("div")({
  display: "contents",
  [mq.tabletP]: { display: "none" },
});

// Mirror of SecondaryActions: the ⋮ overflow trigger exists ONLY below the
// tabletP cut, so tablet-landscape/desktop DOM gains no visible control.
export const OverflowOnly = styled("div")({
  display: "none",
  [mq.tabletP]: {
    display: "flex",
    alignItems: "center",
    // mobile390-header-profilemenu-overflow: the ⋮ trigger's IconWrapper
    // margin (2px 8px, tuned for tablet-landscape/desktop nav rows) is the
    // same control that spills at 390 — tighten it across the WHOLE folded
    // band (now phone + tabletP), not just the narrower <=375 cut below.
    // Scoped to this wrapper's own children so every other IconWrapper use
    // (desktop nav icons, HeaderOverflowMenu's row icons) is unaffected.
    "& > *": { margin: "2px 4px" },
  },
  // M390-01: below NARROW_PHONE_MAX_PX the ⋮ trigger's own IconWrapper margin
  // is tightened further to claw back a few more px right next to the
  // control the sweep measured spilling off-screen — scoped to this
  // wrapper's own children so every OTHER IconWrapper use (desktop nav
  // icons, HeaderOverflowMenu's row icons) is unaffected.
  [`@media (max-width: ${NARROW_PHONE_MAX_PX}px)`]: {
    "& > *": { margin: "2px 2px" },
  },
});
